import {
	addDays,
	differenceInCalendarDays,
	endOfMonth,
	endOfWeek,
	format,
	isWeekend,
	startOfMonth,
	startOfWeek,
} from "date-fns";
import { ptBR } from "date-fns/locale";
import { parseScheduleDate } from "./gantt.ts";
import {
	buildTimelineDomain,
	dateToDayIndex,
	dayIndexToDate,
	type TimelineDomain,
} from "./timeline.ts";

export type TimeScale = "days" | "weeks";

export const PX_PER_DAY: Record<TimeScale, number> = { days: 36, weeks: 12 };

export interface AxisCell {
	key: string;
	label: string;
	sublabel?: string;
	offsetDays: number;
	days: number;
	weekend?: boolean;
	today?: boolean;
}

const WEEK = { weekStartsOn: 1 } as const;

function snapToWeeks(start: Date, end: Date): TimelineDomain {
	const weekStart = startOfWeek(start, WEEK);
	const weekEnd = endOfWeek(end, WEEK);
	return {
		start: weekStart,
		end: weekEnd,
		totalDays: differenceInCalendarDays(weekEnd, weekStart) + 1,
	};
}

export function buildScaleDomain(
	dates: (string | undefined)[],
	today: Date,
	scale: TimeScale,
	extraMonthsBefore = 0,
	extraMonthsAfter = 0,
): TimelineDomain {
	const domain = buildTimelineDomain(
		dates,
		today,
		extraMonthsBefore,
		extraMonthsAfter,
	);
	return scale === "weeks" ? snapToWeeks(domain.start, domain.end) : domain;
}

export function buildPrintDomain(
	dates: (string | undefined)[],
	today: Date,
): TimelineDomain | null {
	const times = dates
		.map(parseScheduleDate)
		.filter((date): date is Date => date !== undefined)
		.map((date) => date.getTime());
	if (times.length === 0) return null;
	times.push(today.getTime());
	return snapToWeeks(
		new Date(Math.min(...times)),
		new Date(Math.max(...times)),
	);
}

export function monthCells(domain: TimelineDomain): AxisCell[] {
	const cells: AxisCell[] = [];
	let cursor = startOfMonth(domain.start);
	while (cursor <= domain.end) {
		const from = cursor < domain.start ? domain.start : cursor;
		const monthEnd = endOfMonth(cursor);
		const to = monthEnd > domain.end ? domain.end : monthEnd;
		const label = format(cursor, "MMMM/yyyy", { locale: ptBR });
		cells.push({
			key: format(cursor, "yyyy-MM"),
			label: label.charAt(0).toUpperCase() + label.slice(1),
			offsetDays: differenceInCalendarDays(from, domain.start),
			days: differenceInCalendarDays(to, from) + 1,
		});
		cursor = addDays(monthEnd, 1);
	}
	return cells;
}

export function dayCells(domain: TimelineDomain, today: Date): AxisCell[] {
	const todayKey = format(today, "yyyy-MM-dd");
	return Array.from({ length: domain.totalDays }, (_, index) => {
		const date = addDays(domain.start, index);
		const key = format(date, "yyyy-MM-dd");
		return {
			key,
			label: format(date, "d"),
			sublabel: format(date, "EEEEEE", { locale: ptBR })
				.replace(".", "")
				.toUpperCase(),
			offsetDays: index,
			days: 1,
			weekend: isWeekend(date),
			today: key === todayKey,
		};
	});
}

export function weekCells(domain: TimelineDomain, today: Date): AxisCell[] {
	const todayKey = format(today, "yyyy-MM-dd");
	const cells: AxisCell[] = [];
	let cursor = domain.start;
	while (cursor <= domain.end) {
		const weekEnd = endOfWeek(cursor, WEEK);
		const to = weekEnd > domain.end ? domain.end : weekEnd;
		const fromKey = format(cursor, "yyyy-MM-dd");
		const toKey = format(to, "yyyy-MM-dd");
		cells.push({
			key: fromKey,
			label: format(startOfWeek(cursor, WEEK), "dd/MM"),
			offsetDays: differenceInCalendarDays(cursor, domain.start),
			days: differenceInCalendarDays(to, cursor) + 1,
			today: fromKey <= todayKey && todayKey <= toKey,
		});
		cursor = addDays(to, 1);
	}
	return cells;
}

export function xOf(
	domain: TimelineDomain,
	dateOnly: string,
	scale: TimeScale,
): number {
	return (dateToDayIndex(domain, dateOnly) ?? 0) * PX_PER_DAY[scale];
}

export function dateAtX(
	domain: TimelineDomain,
	x: number,
	scale: TimeScale,
): string {
	return dayIndexToDate(domain, Math.floor(x / PX_PER_DAY[scale]));
}

export function leftEdgeDate(
	domain: TimelineDomain,
	scrollLeft: number,
	scale: TimeScale,
): string {
	return dateAtX(domain, scrollLeft, scale);
}

export function scrollLeftFor(
	domain: TimelineDomain,
	dateOnly: string,
	scale: TimeScale,
): number {
	return xOf(domain, dateOnly, scale);
}
